"use client";
import { Input } from "@/components/ui/input";
import useBasket from "@/hooks/use-basket";
import { siteDataAtom, storeDataAtom } from "@/lib/atoms";
import { cn } from "@/lib/utils";
import { useAtomValue } from "jotai";
import _ from "lodash";
import { ChevronUp, Minus, Search } from "lucide-react";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import JsonView from "react18-json-view";
import "react18-json-view/src/dark.css";
import "react18-json-view/src/style.css";

type Props = {
  children: React.ReactNode;
};

const DevModeProvider = ({ children }: Props) => {
  return (
    <>
      {process.env.NEXT_PUBLIC_DEV_MODE === "true" && <DevMode />}
      {children}
    </>
  );
};

const DevMode = () => {
  const store = useAtomValue(storeDataAtom);
  const site = useAtomValue(siteDataAtom);
  // const { user } = useAuth({ middleware: "guest" });
  const { basket } = useBasket();
  const [isOpen, setIsOpen] = useState(true);
  const [search, setSearch] = useState("");

  const filteredDatas = useMemo(() => {
    if (search.length < 3) {
      return {
        store,
        site,
        // user,
        basket,
      };
    }

    return {
      store: filterObject(store, search),
      site: filterObject(site, search),
      // user: filterObject(user, search),
      basket: filterObject(basket, search),
    };
  }, [search, store, site, basket]);

  useEffect(() => {
    if (search.length < 3) return;
  }, [search]);

  return (
    <div className={cn("fixed bottom-0 right-0 z-[1000] text-xs")}>
      <button className="border p-1 bg-card/80 text-card-foreground" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <Minus className="size-3" /> : <ChevronUp className="size-3" />}
      </button>
      <div className={cn("bg-card/80 text-card-foreground backdrop-blur-sm drop-shadow-xl p-2 transition-all duration-500 ease-in-out", !isOpen && "h-0 py-0")}>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." startIcon={<Search className="size-3" />} className="h-8 mb-2" />
        <div className="max-h-[300px] overflow-y-auto grid grid-cols-[1fr_auto] gap-2">
          {Object.entries(filteredDatas).map(([key, value]) => (
            <Fragment key={key}>
              <div className="static">
                <p className="sticky top-0">{key}</p>
              </div>
              <JsonView src={value} collapsed={0} collapseStringMode="directly" collapseStringsAfterLength={20} className="max-w-[500px] overflow-auto" />
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * object içindeki key veya value'da arama yapar (case-insensitive).
 * Eşleşen path'leri döner. (örneğin: ["user.name", "posts[0].title"])
 */
function searchObjectPaths(obj: any, term: string): string[] {
  const lowerTerm = term.toLowerCase();
  const matches: string[] = [];

  function walk(current: any, path: string[] = []) {
    if (_.isArray(current)) {
      current.forEach((val, i) => walk(val, [...path, i.toString()]));
    } else if (_.isPlainObject(current)) {
      _.forOwn(current, (val, key) => {
        const currentPath = [...path, key];
        // key eşleşmesi
        if (key.toLowerCase().includes(lowerTerm)) {
          matches.push(currentPath.join("."));
        }
        // value eşleşmesi
        if (_.isString(val) || _.isNumber(val) || _.isBoolean(val)) {
          if (String(val).toLowerCase().includes(lowerTerm)) {
            matches.push(currentPath.join("."));
          }
        }
        // nested obje veya array
        if (_.isObject(val)) walk(val, currentPath);
      });
    }
  }

  walk(obj);
  return _.uniq(matches);
}

/**
 * Arama terimine göre objeyi filtreler (nested yapı korunur).
 * Sadece key veya value'da term geçen alanlar kalır.
 */
function filterObject(obj: any, term: string): any {
  const lowerTerm = term.toLowerCase();

  if (_.isArray(obj)) {
    const arr = obj.map((item) => filterObject(item, term)).filter((val) => !_.isEmpty(val));
    return arr.length ? arr : undefined;
  }

  if (_.isPlainObject(obj)) {
    const result: Record<string, any> = {};

    _.forOwn(obj, (val, key) => {
      const keyMatch = key.toLowerCase().includes(lowerTerm);
      const valueMatch = (_.isString(val) || _.isNumber(val) || _.isBoolean(val)) && String(val).toLowerCase().includes(lowerTerm);

      if (keyMatch || valueMatch) {
        result[key] = val;
      } else if (_.isObject(val)) {
        const nested = filterObject(val, term);
        if (!_.isEmpty(nested)) {
          result[key] = nested;
        }
      }
    });

    return _.isEmpty(result) ? undefined : result;
  }

  return String(obj).toLowerCase().includes(lowerTerm) ? obj : undefined;
}

export default DevModeProvider;
